import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Settings, Mail, Phone, MoreVertical, Trash2, Pencil, ChevronDown, CalendarClock, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ContactDialog from "@/components/ContactDialog";
import SettingsDialog from "@/components/SettingsDialog";

export default function RelationshipDashboard() {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all | group:<id> | status:<id> | needs_followup | recently_contacted | upcoming
  const [editing, setEditing] = useState(null);
  const [openContact, setOpenContact] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [expanded, setExpanded] = useState({});

  const reload = async () => {
    const [c, g, s] = await Promise.all([
      api.get("/contacts"),
      api.get("/groups"),
      api.get("/statuses"),
    ]);
    setContacts(c.data);
    setGroups(g.data);
    setStatuses(s.data);
  };

  useEffect(() => {
    reload();
  }, []);

  const groupMap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const statusMap = useMemo(() => Object.fromEntries(statuses.map((s) => [s.id, s])), [statuses]);

  const filtered = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let list = [...contacts];
    if (activeTab.startsWith("group:")) {
      const gid = activeTab.split(":")[1];
      list = list.filter((c) => c.group_id === gid);
    } else if (activeTab.startsWith("status:")) {
      const sid = activeTab.split(":")[1];
      list = list.filter((c) => c.status_id === sid);
    } else if (activeTab === "needs_followup") {
      list = list.filter((c) => c.next_follow_up_date && c.next_follow_up_date <= today);
    } else if (activeTab === "recently_contacted") {
      const cutoff = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
      list = list.filter((c) => c.last_contact_date && c.last_contact_date >= cutoff);
    } else if (activeTab === "upcoming") {
      list = list.filter((c) => c.next_follow_up_date && c.next_follow_up_date >= today);
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (c) =>
          c.full_name?.toLowerCase().includes(s) ||
          c.company?.toLowerCase().includes(s) ||
          c.email?.toLowerCase().includes(s) ||
          c.phone?.toLowerCase().includes(s) ||
          c.role_industry?.toLowerCase().includes(s) ||
          (c.tags || []).some((t) => t.toLowerCase().includes(s)),
      );
    }
    return list;
  }, [contacts, activeTab, q]);

  const remove = async (id) => {
    if (!window.confirm("Delete this contact? This cannot be undone.")) return;
    await api.delete(`/contacts/${id}`);
    setContacts((prev) => prev.filter((c) => c.id !== id));
    toast.success("Contact deleted");
  };

  const onSaved = (saved) => {
    if (saved && saved.id) {
      setContacts((prev) => {
        const i = prev.findIndex((c) => c.id === saved.id);
        if (i >= 0) {
          const copy = [...prev];
          copy[i] = saved;
          return copy;
        }
        return [saved, ...prev];
      });
    } else {
      reload();
    }
  };

  return (
    <div className="px-10 py-9 mr-fade-up">
      <header className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-3xl text-slate-900 tracking-tight">Relationship Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1 font-body">
            {contacts.length} contacts · {groups.length} groups
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpenSettings(true)}
            data-testid="open-settings-button"
            className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-heading text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Customize
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setOpenContact(true);
            }}
            data-testid="new-contact-button"
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-heading font-medium hover:bg-slate-800 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New contact
          </button>
        </div>
      </header>

      <div className="mr-card p-3 mb-5 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400 ml-2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, company, email, phone, role, tag…"
          className="flex-1 bg-transparent border-0 outline-none text-sm font-body placeholder:text-slate-400 py-2"
          data-testid="contacts-search-input"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Tab label="All" active={activeTab === "all"} onClick={() => setActiveTab("all")} testid="filter-all" />
        <Tab label="Needs follow-up" active={activeTab === "needs_followup"} onClick={() => setActiveTab("needs_followup")} testid="filter-followup" />
        <Tab label="Upcoming" active={activeTab === "upcoming"} onClick={() => setActiveTab("upcoming")} testid="filter-upcoming" />
        <Tab label="Recently contacted" active={activeTab === "recently_contacted"} onClick={() => setActiveTab("recently_contacted")} testid="filter-recent" />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        {groups.map((g) => (
          <Tab
            key={g.id}
            label={g.name}
            color={g.color}
            active={activeTab === `group:${g.id}`}
            onClick={() => setActiveTab(`group:${g.id}`)}
            testid={`filter-group-${g.id}`}
          />
        ))}
        <span className="w-px h-5 bg-slate-200 mx-1" />
        {statuses.map((s) => (
          <Tab
            key={s.id}
            label={s.name}
            color={s.color}
            active={activeTab === `status:${s.id}`}
            onClick={() => setActiveTab(`status:${s.id}`)}
            testid={`filter-status-${s.id}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
        {filtered.map((c) => {
          const g = groupMap[c.group_id];
          const s = statusMap[c.status_id];
          const isOpen = !!expanded[c.id];
          const today = new Date().toISOString().slice(0, 10);
          const overdue = c.next_follow_up_date && c.next_follow_up_date < today;
          return (
            <div key={c.id} className="mr-card mr-card-hover p-5 group" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: g?.color || "#cbd5e1" }} title={g?.name} />
                  <h3 className="font-heading text-lg text-slate-900 truncate">{c.full_name}</h3>
                </div>
                {s && (
                  <span className="mr-pill border-transparent text-xs whitespace-nowrap" style={{ backgroundColor: `${s.color}1a`, color: s.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </span>
                )}
              </div>

              <div className="text-sm text-slate-600 truncate flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                {c.company || "—"}{c.role_industry ? ` · ${c.role_industry}` : ""}
              </div>

              <div className="mt-3 grid gap-1 text-sm text-slate-600">
                {c.email && (
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 hover:text-slate-900 truncate">
                    <Mail className="w-3.5 h-3.5 text-slate-400" /> {c.email}
                  </a>
                )}
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 hover:text-slate-900">
                    <Phone className="w-3.5 h-3.5 text-slate-400" /> {c.phone}
                  </a>
                )}
                {c.next_follow_up_date && (
                  <div className={`inline-flex items-center gap-2 ${overdue ? "text-rose-600" : "text-slate-600"}`}>
                    <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
                    {overdue ? "Follow up overdue · " : "Follow up · "} {c.next_follow_up_date}
                  </div>
                )}
              </div>

              {(c.tags || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.tags.slice(0, 6).map((t) => (
                    <span key={t} className="text-xs text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 mr-fade-up">
                  {(c.notes || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-1.5">Notes</div>
                      <div className="space-y-1.5">
                        {(c.notes || []).slice(0, 3).map((n) => (
                          <div key={n.id} className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 line-clamp-3">{n.text}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(c.tasks || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-1.5">Tasks</div>
                      <div className="space-y-1">
                        {(c.tasks || []).slice(0, 4).map((t) => (
                          <div key={t.id} className={`text-sm flex items-center gap-2 ${t.completed ? "line-through text-slate-400" : "text-slate-600"}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            {t.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                <button
                  onClick={() => setExpanded((m) => ({ ...m, [c.id]: !m[c.id] }))}
                  className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 font-heading"
                  data-testid={`expand-contact-${c.id}`}
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  {isOpen ? "Collapse" : "Expand"}
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditing(c);
                      setOpenContact(true);
                    }}
                    className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    data-testid={`edit-contact-${c.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    className="p-1.5 rounded-md text-slate-500 hover:text-rose-500 hover:bg-rose-50"
                    data-testid={`delete-contact-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-span-full text-center py-20 text-slate-400 font-body text-sm">
            No contacts match your filters.
          </div>
        )}
      </div>

      <ContactDialog
        open={openContact}
        onClose={() => setOpenContact(false)}
        contact={editing}
        groups={groups}
        statuses={statuses}
        onSaved={onSaved}
      />
      <SettingsDialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        groups={groups}
        statuses={statuses}
        onChanged={reload}
      />
    </div>
  );
}

function Tab({ label, active, onClick, color, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`mr-pill border transition-all ${
        active
          ? "bg-slate-900 border-slate-900 text-white"
          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900"
      }`}
    >
      {color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />}
      {label}
    </button>
  );
}
